import React, { useState } from 'react';
import { Formik } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { keys, length } from 'ramda';
import { Autorenew, FileCopy } from '@mui/icons-material';
import { Box, Checkbox, FormControlLabel, SelectChangeEvent } from '@mui/material';
import { AlertBoxMsg, Button, SecretField, SelectField, TextField } from '@qrios/frontend-library';

import CopyToClipboard from 'react-copy-to-clipboard';
import Tooltip from '../../../../../../common/tooltip';
import { convertBooleanToEnum } from '../../../../../../../api/utils';
import WithFormLayout from '../../../../../../common/with-form-layout';
import { BusinessError } from '../../../../../../common/business-error';
import { diffObject } from '../../../../../../../constants/helpers/diff';
import { TOGGLE_AUTH_METHOD, TOGGLE_DEBUG_MODE, TOGGLE_STATUS } from '../../../../../../common/status/interfaces';
import { RADIUS } from '../../../../../../../styles/themes';

import locales from '../locales';
import { Params, VoiceAppEditData, VoiceAppFormData } from '../interfaces';
import { editFormValidations } from './validationSchemas';
import ProcessEditor from '../components/processEditor';
import { useDialog } from '../../../../../../common/dialog/useDialog';
import { Dialog } from '../../../../../../common/dialog';
import ProcessView from '../components/processView';

interface Props {
  onSubmit: (voiceAppSettings: VoiceAppEditData) => void;
  onClose: () => void;
  loading: boolean;
  initialVoiceAppFormData: VoiceAppEditData;
  error?: BusinessError | null;
  popup: {
    setStateCallback(state: boolean): void;
    setMsgCallback(msg: string): void;
  };
}

const EditAppForm = ({ initialVoiceAppFormData, onSubmit, onClose, loading, error, popup }: Props) => {
  const [regeneratedApi, setRegeneratedApi] = useState(false);
  const [regenerateApiKeyConfirmed, setRegenerateApiKeyConfirmed] = useState(false);

  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();

  const toggleClientApiKeyRegenerateButton = () => {
    setRegenerateApiKeyConfirmed(!regenerateApiKeyConfirmed);
  };
  const handleClipboard = () => {
    popup.setStateCallback(true);
    popup.setMsgCallback(`${locales.form.apiKey.label} copied to clipboard`);
  };

  return (
    <Formik
      initialValues={{
        ...initialVoiceAppFormData,
        debugEnabled: convertBooleanToEnum(initialVoiceAppFormData.debugEnabled),
        authorizationMethod: initialVoiceAppFormData.authorizationMethod.type,
        apiKey: '',
        processId: initialVoiceAppFormData.process.id,
        params: { ...initialVoiceAppFormData.process.params }
      }}
      validationSchema={editFormValidations}
      onSubmit={values => {
        let voiceAppFormData: VoiceAppFormData = {
          appId: initialVoiceAppFormData.appId,
          appUrl: values.appUrl,
          status: values.status,
          debugEnabled: values.debugEnabled === TOGGLE_DEBUG_MODE.ENABLED,
          authorizationMethod: initialVoiceAppFormData.authorizationMethod,
          process: {
            id: values.processId,
            params: values.params
          }
        };

        const authorizationWasChanged =
          initialVoiceAppFormData.authorizationMethod?.type !== values.authorizationMethod;
        const apiKeyWasChanged = values.apiKey.length > 1;

        if (authorizationWasChanged || apiKeyWasChanged) {
          if (values.authorizationMethod === TOGGLE_AUTH_METHOD.API_KEY) {
            voiceAppFormData = {
              ...voiceAppFormData,
              authorizationMethod: { type: values.authorizationMethod, key: values.apiKey }
            };
          } else {
            voiceAppFormData = {
              ...voiceAppFormData,
              authorizationMethod: { type: values.authorizationMethod }
            };
          }
        }

        onSubmit(voiceAppFormData);
      }}
    >
      {({ values, initialValues, handleChange, handleSubmit, errors, touched, handleBlur, setFieldValue }) => {
        const valuesUnchanged = length(keys(diffObject(initialValues, values))) === 0;
        const apiKeyIsNotValid =
          values.authorizationMethod === TOGGLE_AUTH_METHOD.API_KEY &&
          values.apiKey.length === 0 &&
          initialVoiceAppFormData.authorizationMethod?.type !== values.authorizationMethod;
        //   form behavior
        const confirmedCheckboxIsActive =
          initialVoiceAppFormData.authorizationMethod?.type === TOGGLE_AUTH_METHOD.API_KEY;
        const changedKeyWarning = initialVoiceAppFormData.authorizationMethod?.type === TOGGLE_AUTH_METHOD.API_KEY;
        const infoBoxAboutApiKeyIsVisible = values.authorizationMethod === TOGGLE_AUTH_METHOD.API_KEY;
        const alertBoxAboutApiKeyIsVisible =
          regeneratedApi && values.authorizationMethod === TOGGLE_AUTH_METHOD.API_KEY;
        const alertBoxAboutResignFromApiKeyIsVisible =
          initialVoiceAppFormData.authorizationMethod?.type === TOGGLE_AUTH_METHOD.API_KEY &&
          values.authorizationMethod === TOGGLE_AUTH_METHOD.NO_AUTHORIZATION;
        const apiKeyInputIsVisible = values.authorizationMethod === TOGGLE_AUTH_METHOD.API_KEY;
        const submitButtonIsDanger =
          (regeneratedApi &&
            values.authorizationMethod === TOGGLE_AUTH_METHOD.API_KEY &&
            initialVoiceAppFormData.authorizationMethod?.type === TOGGLE_AUTH_METHOD.API_KEY) ||
          alertBoxAboutResignFromApiKeyIsVisible;

        const processParamsUnchanged = length(keys(diffObject(initialValues.process.params, values.params))) === 0;
        const submitButtonIsDisabled = (valuesUnchanged || apiKeyIsNotValid) && processParamsUnchanged;

        const handleProcessSubmit = (params: Params) => {
          setFieldValue('params', { ...params });
          handleCloseDialog();
        };

        return (
          <WithFormLayout
            formInfoText={
              <>
                Set new values for <b>{initialVoiceAppFormData.appId}</b>
              </>
            }
            onSubmit={handleSubmit}
            submitLabel="Edit Voice app"
            secondaryLabel={valuesUnchanged ? 'Close' : 'Close without update'}
            onSecondary={onClose}
            loading={loading}
            error={error}
            danger={submitButtonIsDanger}
            disableSubmit={submitButtonIsDisabled}
          >
            {infoBoxAboutApiKeyIsVisible && (
              <Box mb={2}>
                <AlertBoxMsg severity="info">
                  <p>
                    Authorisation by API key will be active sent with all USSD API requests to your application. If you
                    want to update the API key for this application you need to click the <b>Regenerate</b> icon. API
                    Key will be used immediately after you click the
                    <b> Edit Voice App</b> button.
                  </p>
                </AlertBoxMsg>
              </Box>
            )}
            {alertBoxAboutApiKeyIsVisible && changedKeyWarning && (
              <Box mb={2}>
                <AlertBoxMsg severity="warning">
                  <p>
                    You created a new API key. Click <b>Edit Voice app</b> to activate it in your application. Your old
                    API key from this moment will be invalid. To abort all your changes, click{' '}
                    <b>Close without update</b> button.
                  </p>
                </AlertBoxMsg>
              </Box>
            )}
            {alertBoxAboutResignFromApiKeyIsVisible && (
              <Box mb={2}>
                <AlertBoxMsg severity="warning">
                  <p>
                    You have opted out of the API Key authentication method. When you save your changes, your
                    application, which implements USSD API, will stop receiving Authorization header with the token. It
                    won’t be possible to revert the change with the same value of the token (the new one will be
                    generated). Your application may require reconfiguration on your side.To abort all your changes,
                    click <b>Cancel</b> without save button.
                  </p>
                </AlertBoxMsg>
              </Box>
            )}
            <Box mb={2}>
              <TextField
                handleBlur={handleBlur}
                handleChange={handleChange}
                name="appUrl"
                label={locales.form.appUrl.label}
                placeholder={locales.form.appUrl.placeholder}
                value={values.appUrl}
                error={errors.appUrl}
                touched={touched.appUrl}
              />
            </Box>
            <Box mb={2}>
              <SelectField
                name="status"
                label={locales.form.status.label}
                value={values.status}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={errors.status}
                touched={touched.status}
                items={[
                  { label: 'Online', value: TOGGLE_STATUS.ENABLED },
                  { label: 'Offline', value: TOGGLE_STATUS.DISABLED }
                ]}
              />
            </Box>
            <Box mb={2}>
              <SelectField
                name="debugEnabled"
                label={locales.form.debugEnabled.label}
                value={values.debugEnabled}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={errors.debugEnabled}
                touched={touched.debugEnabled}
                items={[
                  { label: 'Debug mode active', value: TOGGLE_DEBUG_MODE.ENABLED },
                  { label: 'Debug mode disabled', value: TOGGLE_DEBUG_MODE.DISABLED }
                ]}
              />
            </Box>
            <Box mb={2}>
              <TextField
                handleBlur={handleBlur}
                handleChange={handleChange}
                name="processId"
                label={locales.form.process.label}
                placeholder={locales.form.process.placeholder}
                value={values.processId}
                error={errors.processId}
                touched={touched.processId}
              />
              <ProcessView params={values.params} />
              {isDialogOpen && (
                <Dialog open={isDialogOpen} title="Define process parameters" handleClose={handleCloseDialog}>
                  <ProcessEditor
                    onSubmit={handleProcessSubmit}
                    params={values.params}
                    handleClose={handleCloseDialog}
                  />
                </Dialog>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Button
                  style={{ width: '100%', marginBottom: '16px' }}
                  text="Define Process Params"
                  onClick={handleOpenDialog}
                />
              </div>
            </Box>
            <Box mb={2}>
              <SelectField
                name="authorizationMethod"
                label={locales.form.authorizationMethod.label}
                value={values.authorizationMethod}
                handleChange={(
                  e: SelectChangeEvent<{
                    name: string;
                    value: string;
                  }>
                ) => {
                  if (
                    initialVoiceAppFormData.authorizationMethod?.type === TOGGLE_AUTH_METHOD.NO_AUTHORIZATION &&
                    e.target.value === TOGGLE_AUTH_METHOD.API_KEY
                  ) {
                    setFieldValue('apiKey', uuidv4());
                    setRegeneratedApi(true);
                  }
                  handleChange(e);
                }}
                handleBlur={handleBlur}
                error={errors.authorizationMethod}
                touched={touched.authorizationMethod}
                items={[
                  { label: 'No authorization', value: TOGGLE_AUTH_METHOD.NO_AUTHORIZATION },
                  { label: 'API key', value: TOGGLE_AUTH_METHOD.API_KEY }
                ]}
              />
            </Box>

            {apiKeyInputIsVisible && (
              <Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
                  <SecretField
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    name="apiKey"
                    label={locales.form.apiKey.label}
                    value={values.apiKey}
                    touched={touched.apiKey}
                    disabled={true}
                    placeholder="Key configured and secured"
                    // eslint-disable-next-line max-len
                    helpInfo="For security reasons, you can't see the current API key. It is correctly configured and stored on our server."
                  />
                  <Tooltip title="Regenerate API Key" placement="bottom-end">
                    <Box sx={{ paddingLeft: '16px', paddingTop: '28px' }}>
                      <Button
                        text=""
                        onClick={() => {
                          setFieldValue('apiKey', uuidv4());
                          setRegeneratedApi(true);
                        }}
                        endIcon={<Autorenew sx={{ fontSize: '25px !important' }} />}
                        style={{ height: '47px', borderRadius: `${RADIUS.sm}px`, paddingLeft: 'revert' }}
                        disabled={!regenerateApiKeyConfirmed && confirmedCheckboxIsActive}
                      />
                    </Box>
                  </Tooltip>
                  <Tooltip title="Copy to clipboard">
                    <Box sx={{ paddingLeft: '16px', paddingTop: '28px' }}>
                      <CopyToClipboard text={values.apiKey}>
                        <Button
                          text=""
                          onClick={handleClipboard}
                          endIcon={<FileCopy sx={{ fontSize: '25px !important' }} />}
                          style={{ height: '47px', borderRadius: `${RADIUS.sm}px`, paddingLeft: 'revert' }}
                          disabled={!regeneratedApi || apiKeyIsNotValid}
                        />
                      </CopyToClipboard>
                    </Box>
                  </Tooltip>
                </Box>
                {confirmedCheckboxIsActive && (
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={regenerateApiKeyConfirmed}
                          onChange={toggleClientApiKeyRegenerateButton}
                          name="dangerConfirm"
                          color="primary"
                          data-testid="checkbox-danger-confirm-apps"
                        />
                      }
                      // eslint-disable-next-line max-len
                      label="Confirm to get access to edit API Key. Please note that after updating and clicking Edit Voice app, the old key will be invalid."
                      data-testid="apps-checbox-section"
                    />
                  </Box>
                )}
              </Box>
            )}
          </WithFormLayout>
        );
      }}
    </Formik>
  );
};

export default EditAppForm;
